/**
 * Escritas da página de Financiamentos pelos repositórios do app: criar/editar/arquivar/excluir
 * contrato, abatimento (os três modos, ADR 0057), pagar parcela (quitar, pagamento parcial,
 * encargos), "ajustar projeção ao valor real" (ADR 0081) e desfazer. As validações de negócio
 * moram no repositório; aqui só o mapeamento — e o id do lançamento parcial, que o repositório
 * não devolve e o Desfazer precisa.
 */
import { and, eq, isNull } from 'drizzle-orm';
import { db } from '@/db/client';
import { transactions } from '@/db/schema';
import {
  archiveFinancing, createFinancing, deleteFinancing, deleteFinancingAdjustment, deleteFinancingPartialPayment,
  recalibrateFinancingCurve, recordFinancingPartialPayment, recordFinancingPrepayment, setFinancingInstallmentCharges, updateFinancing,
} from '@/db/repositories/financingRepository';
import { updateTransaction } from '@/db/repositories/transactionRepository';
import type { FinancingsEditor } from '../viewer/lib/editing';

/** ids dos pagamentos parciais vivos do contrato (o repositório devolve o contrato, não o lançamento criado) */
async function partialIds(financingId: string): Promise<Set<string>> {
  const rows = await db.select({ id: transactions.id }).from(transactions)
    .where(and(eq(transactions.financingId, financingId), eq(transactions.financingEntryType, 'partial_payment'), isNull(transactions.deletedAt)));
  return new Set(rows.map(r => r.id));
}

export function createFinancingsEditor(after: () => Promise<void>): FinancingsEditor {
  const done = async <T,>(v: T): Promise<T> => { await after(); return v; };
  return {
    async create(input) {
      const f = await createFinancing({
        name: input.name, kind: input.kind, institution: input.institution, paymentAccountId: input.paymentAccountId,
        categoryId: input.categoryId, subcategoryId: input.subcategoryId, monthlyAmount: input.monthlyAmount,
        totalInstallments: input.totalInstallments, nextInstallmentNumber: input.nextInstallmentNumber, nextDueDate: input.nextDueDate ?? '',
        lastKnownBalance: input.balance, amortizationSystem: input.amortizationSystem, annualRate: input.annualRate,
        curveBalance: input.balance, curveExtraAmount: input.curveExtraAmount,
      });
      return done(f.id);
    },
    async update(id, input) {
      await updateFinancing({
        financingId: id, name: input.name, kind: input.kind, institution: input.institution, paymentAccountId: input.paymentAccountId,
        monthlyAmount: input.monthlyAmount, nextDueDate: input.nextDueDate, totalInstallments: input.totalInstallments,
        categoryId: input.categoryId, subcategoryId: input.subcategoryId,
        amortizationSystem: input.amortizationSystem, annualRate: input.annualRate, curveBalance: input.balance, curveExtraAmount: input.curveExtraAmount,
      });
      await after();
    },
    async archive(id) { await archiveFinancing(id); await after(); },
    async remove(id) { await deleteFinancing(id); await after(); },
    async prepay(input) {
      await recordFinancingPrepayment({
        financingId: input.financingId, date: input.date, amount: input.amount, remainingInstallments: input.remainingInstallments,
        newOutstandingBalance: input.newOutstandingBalance, notes: input.notes, reductionMode: input.mode,
        newMonthlyAmount: input.mode === 'installment' ? input.newMonthlyAmount : null, withoutCashMovement: input.withoutCashMovement,
      });
      await after();
    },
    async removeAdjustment(id) { await deleteFinancingAdjustment(id); await after(); },
    async settle(input) {
      // Parcela PENDENTE aceita valor/data/conta no fluxo de pagamento (antecipação com desconto, pagar por outra conta).
      await updateTransaction(input.transactionId, { isConfirmed: true, amount: input.amount, date: input.date, accountId: input.accountId } as Parameters<typeof updateTransaction>[1], 'single');
      await after();
    },
    async unsettle(transactionId, restore) {
      const back = restore ? { date: restore.date, amount: restore.amount, ...(restore.accountId ? { accountId: restore.accountId } : {}) } : {};
      await updateTransaction(transactionId, { isConfirmed: false, ...back } as Parameters<typeof updateTransaction>[1], 'single');
      await after();
    },
    async partialPayment(input) {
      const was = await partialIds(input.financingId);
      await recordFinancingPartialPayment({ financingId: input.financingId, transactionId: input.transactionId, amount: input.amount, date: input.date, accountId: input.accountId });
      const now = await partialIds(input.financingId);
      return done([...now].find(id => !was.has(id)) ?? null);
    },
    async removePartialPayment(transactionId) { await deleteFinancingPartialPayment(transactionId); await after(); },
    async setCharges(input) { await setFinancingInstallmentCharges(input); await after(); },
    async recalibrate(input) { await recalibrateFinancingCurve(input); await after(); },
  };
}
